import { useState, useEffect, useCallback } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import { getTokenPayload } from "../../components/ProtectedRoute";
import adminFetch from "../../utils/adminFetch";

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "200px" }}>
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

function timeAgo(str) {
  if (!str) return "—";
  const diff = Date.now() - new Date(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(str).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const ROLE_CONFIG = {
  "super-admin": { label: "Super Admin", bg: "#FEF3C7", color: "#92400E" },
  "admin":       { label: "Admin",       bg: "#DBEAFE", color: "#1D4ED8" },
  "unknown":     { label: "Unknown",     bg: "#F3F4F6", color: "#6B7280" },
};

function RoleBadge({ role }) {
  const c = ROLE_CONFIG[role] || ROLE_CONFIG.unknown;
  return (
    <span style={{ background: c.bg, color: c.color, padding: "2px 10px", borderRadius: "20px",
      fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
      fontFamily: "Arial, sans-serif" }}>
      {c.label}
    </span>
  );
}

function StatusBadge({ success }) {
  return (
    <span style={{
      background: success ? "#D1FAE5" : "#FEE2E2",
      color: success ? "#065F46" : "#991B1B",
      padding: "2px 10px", borderRadius: "20px",
      fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
      fontFamily: "Arial, sans-serif",
    }}>
      {success ? "Success" : "Failed"}
    </span>
  );
}

export default function AdminLoginLogs() {
  usePageTitle("Login Logs");

  const currentAdmin = getTokenPayload();
  const isSuperAdmin = currentAdmin?.role === "super-admin";

  const [logs,    setLogs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [page,    setPage]    = useState(1);
  const [filter,  setFilter]  = useState("all"); // all | success | failed

  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await adminFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/login-logs?page=${page}&limit=${LIMIT}`
      );
      const data = await res.json();
      if (data.success) {
        setLogs(data.logs);
        setTotal(data.total);
      } else {
        setError(data.message || "Failed to load logs.");
      }
    } catch {
      setError("Failed to load login logs.");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all"
    ? logs
    : logs.filter((l) => filter === "success" ? l.success : !l.success);

  const successCount = logs.filter((l) => l.success).length;
  const failCount    = logs.filter((l) => !l.success).length;

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={{ maxWidth: "960px" }}>

      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "22px", letterSpacing: "0.06em" }}>
            Login Logs
          </h1>
          <p className="font-constantia mt-1" style={{ fontSize: "14px", color: "#94a3b8" }}>
            {isSuperAdmin ? "All admin login activity." : "Your recent login activity."}
            {" "}Records auto-expire after 90 days.
          </p>
        </div>
        <button
          onClick={() => { setPage(1); load(); }}
          className="flex items-center gap-2 font-cinzel uppercase py-2.5 px-4 rounded-xl transition-all duration-200"
          style={{ fontSize: "10px", letterSpacing: "0.15em", border: "1px solid rgba(1,46,114,0.12)", color: "#012E72", background: "#fff" }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Entries", value: total, color: "#012E72", bg: "#EEF2F8" },
          { label: "Successful",    value: successCount, color: "#065F46", bg: "#D1FAE5" },
          { label: "Failed",        value: failCount,    color: "#991B1B", bg: "#FEE2E2" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl p-5" style={{ background: "#fff", border: "1px solid rgba(1,46,114,0.08)" }}>
            <p className="font-cinzel uppercase mb-1" style={{ fontSize: "9px", letterSpacing: "0.22em", color: "#94a3b8" }}>{s.label}</p>
            <p className="font-cinzel font-bold" style={{ fontSize: "28px", color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { key: "all",     label: "All" },
          { key: "success", label: "Successful" },
          { key: "failed",  label: "Failed" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className="font-cinzel uppercase px-4 py-2 rounded-xl transition-all duration-150"
            style={{
              fontSize: "9px", letterSpacing: "0.15em",
              background: filter === tab.key ? "linear-gradient(135deg,#012E72,#002DB5)" : "#fff",
              color: filter === tab.key ? "#fff" : "#94a3b8",
              border: filter === tab.key ? "none" : "1px solid rgba(1,46,114,0.1)",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ background: "#fff", border: "1px solid rgba(1,46,114,0.08)" }}>

        {/* Column headers */}
        <div
          className="grid px-5 py-3"
          style={{
            gridTemplateColumns: isSuperAdmin
              ? "1fr 120px 90px 140px 120px 120px"
              : "1fr 90px 140px 120px 120px",
            borderBottom: "2px solid #f1f5f9",
          }}
        >
          {[
            isSuperAdmin && "User",
            "Role",
            "Result",
            "IP Address",
            "Reason",
            "Time",
          ].filter(Boolean).map((h) => (
            <span key={h} className="font-cinzel uppercase" style={{ fontSize: "9px", letterSpacing: "0.2em", color: "#94a3b8" }}>{h}</span>
          ))}
        </div>

        {error && (
          <div className="mx-6 mt-4 mb-2 px-4 py-3 rounded-xl" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
            <p className="font-constantia text-red-600" style={{ fontSize: "13px" }}>{error}</p>
          </div>
        )}

        {loading ? (
          <Spinner />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <p className="font-cinzel uppercase" style={{ fontSize: "10px", letterSpacing: "0.2em", color: "#cbd5e1" }}>
              No logs found
            </p>
          </div>
        ) : (
          filtered.map((log, i) => (
            <div
              key={log._id}
              className="grid items-center px-5 transition-colors duration-100"
              style={{
                gridTemplateColumns: isSuperAdmin
                  ? "1fr 120px 90px 140px 120px 120px"
                  : "1fr 90px 140px 120px 120px",
                borderBottom: i < filtered.length - 1 ? "1px solid #f8fafc" : "none",
                background: log.success ? "transparent" : "rgba(220,38,38,0.02)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f7f9ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = log.success ? "transparent" : "rgba(220,38,38,0.02)"; }}
            >
              {/* User (super-admin only) */}
              {isSuperAdmin && (
                <div style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                  <p className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "12px", letterSpacing: "0.05em" }}>
                    {log.name || "—"}
                  </p>
                  <p className="font-constantia" style={{ fontSize: "11px", color: "#94a3b8" }}>
                    @{log.username}
                  </p>
                </div>
              )}

              {/* Role */}
              <div style={{ padding: "14px 16px" }}>
                <RoleBadge role={log.role} />
              </div>

              {/* Result */}
              <div style={{ padding: "14px 16px" }}>
                <StatusBadge success={log.success} />
              </div>

              {/* IP */}
              <div style={{ padding: "14px 16px" }}>
                <p className="font-constantia" style={{ fontSize: "12px", color: "#475569", fontFamily: "'Courier New', monospace" }}>
                  {log.ip || "—"}
                </p>
              </div>

              {/* Reason */}
              <div style={{ padding: "14px 16px" }}>
                <p className="font-constantia" style={{ fontSize: "12px", color: log.reason ? "#dc2626" : "#94a3b8" }}>
                  {log.reason || "—"}
                </p>
              </div>

              {/* Time */}
              <div style={{ padding: "14px 16px" }}>
                <p className="font-constantia" style={{ fontSize: "11px", color: "#94a3b8" }} title={formatDate(log.createdAt)}>
                  {timeAgo(log.createdAt)}
                </p>
                <p className="font-constantia" style={{ fontSize: "10px", color: "#cbd5e1" }}>
                  {formatDate(log.createdAt)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="font-constantia" style={{ fontSize: "13px", color: "#94a3b8" }}>
            Page {page} of {totalPages} — {total} total entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="font-cinzel uppercase px-4 py-2 rounded-xl transition-all duration-150 disabled:opacity-40"
              style={{ fontSize: "9px", letterSpacing: "0.15em", border: "1px solid rgba(1,46,114,0.12)", color: "#012E72", background: "#fff" }}
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="font-cinzel uppercase px-4 py-2 rounded-xl transition-all duration-150 disabled:opacity-40"
              style={{ fontSize: "9px", letterSpacing: "0.15em", background: "linear-gradient(135deg,#012E72,#002DB5)", color: "#fff" }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
